// Butting products together: a product pushed into another moves
// as far as it can and stops flush against it, sliding along it as the
// pointer moves on, and touching never counts as overlapping. Only the solid
// body of each product gets in the way (ProductFacts.solidBBoxes), so the
// inside of an L-shaped corner and the space under a raised carcase stay
// open.
import * as THREE from 'three';
import {overlapOn} from 'components/customer/RoomPlanner/helpers/boxes';
import {isSameLayoutItem} from 'components/customer/RoomPlanner/helpers/overlap';

// How far two products may run into each other, 2.5 mm, and still count as
// touching rather than overlapping.
export const BUTT_TOLERANCE = 0.5;

// How far a face or corner snap may land a product in its neighbour, 0.05
// mm: one that is right lands flush (CO-5961).
export const FLUSH_TOLERANCE = 0.01;

// How far a snap may land a product in its neighbour, 5 mm, and still count
// as landed: the drag allowed this before butting (checkCabinetOverlap at
// buffer -1), and the product rules' strategies land that close.
export const SNAP_TOLERANCE = 1;

/**
 * A product that may be in the way, by its solid boxes.
 */
export interface Obstacle {
    product: THREE.Group;
    bboxes: readonly THREE.Box3[];
}

/**
 * @param {THREE.Box3 | THREE.Box3[]} moving A product's solid boxes, or one
 * box.
 * @return {THREE.Box3[]} Copies of them, as a list.
 */
const boxesOf = (moving: THREE.Box3 | readonly THREE.Box3[]): THREE.Box3[] =>
    (moving instanceof THREE.Box3 ? [moving] : [...moving]).map((bbox) =>
        bbox.clone()
    );

/**
 * Where a product can get to on its way from where it is to a target: as far
 * along each axis in turn (x, then z, then height) as the boxes in the way
 * allow, so it ends flush against what stopped it and still slides along it.
 * A box it is already in more than SNAP_TOLERANCE deep doesn't stop it, so a
 * product that starts overlapping can move out (CO-5961). Each of its solid
 * boxes is checked on its own, so its open corners stay open, as another
 * product's do.
 * @param {THREE.Box3 | THREE.Box3[]} moving The product's solid boxes where
 * it is.
 * @param {THREE.Vector3} from Where it is.
 * @param {THREE.Vector3} to Where the pointer would put it.
 * @param {THREE.Box3[]} blockers The boxes it may not run into.
 * @return {THREE.Vector3} Where it gets to.
 */
export const buttTowards = (
    moving: THREE.Box3 | readonly THREE.Box3[],
    from: THREE.Vector3,
    to: THREE.Vector3,
    blockers: readonly THREE.Box3[]
): THREE.Vector3 => {
    const position = from.clone();
    const parts = boxesOf(moving);

    [0, 2, 1].forEach((axis) => {
        const move = to.getComponent(axis) - position.getComponent(axis);
        if (Math.abs(move) < 1e-9) return;

        let allowed = move;
        parts.forEach((bbox) => {
            blockers.forEach((other) => {
                const inTheWay = [0, 1, 2]
                    .filter((a) => a !== axis)
                    .every((a) => overlapOn(bbox, other, a) > BUTT_TOLERANCE);
                if (!inTheWay) return;

                if (move > 0) {
                    const gap =
                        other.min.getComponent(axis) -
                        bbox.max.getComponent(axis);
                    if (gap >= -SNAP_TOLERANCE && gap < allowed) {
                        allowed = Math.max(0, gap);
                    }
                } else {
                    const gap =
                        other.max.getComponent(axis) -
                        bbox.min.getComponent(axis);
                    if (gap <= SNAP_TOLERANCE && gap > allowed) {
                        allowed = Math.min(0, gap);
                    }
                }
            });
        });

        position.setComponent(axis, position.getComponent(axis) + allowed);
        const shift = new THREE.Vector3().setComponent(axis, allowed);
        parts.forEach((bbox) => bbox.translate(shift));
    });

    return position;
};

/**
 * @param {THREE.Box3 | THREE.Box3[]} moving A product's solid boxes, or one
 * box.
 * @param {Obstacle[]} others The products it may overlap.
 * @param {number} [tolerance] How far in still counts as touching.
 * @return {THREE.Group[]} Those it really overlaps: by more than the
 * tolerance every way, so touching never counts.
 */
export const overlappingOf = (
    moving: THREE.Box3 | readonly THREE.Box3[],
    others: readonly Obstacle[],
    tolerance = BUTT_TOLERANCE
): THREE.Group[] => {
    const parts = boxesOf(moving);

    return others
        .filter(({bboxes}) =>
            bboxes.some((other) =>
                parts.some((bbox) =>
                    [0, 1, 2].every(
                        (axis) => overlapOn(bbox, other, axis) > tolerance
                    )
                )
            )
        )
        .map(({product}) => product);
};

/**
 * @param {THREE.Object3D} product A product.
 * @return {boolean} True for an inner drawer only, which belongs inside
 * another product.
 */
const belongsInside = ({userData}: THREE.Object3D): boolean =>
    Boolean(userData.innerDrawerOnly);

/**
 * @param {THREE.Group} dragged The dragged product.
 * @param {THREE.Group} other Another product.
 * @return {boolean} True unless one belongs inside the other (CO-5961).
 * Bodies leave the space under a raised carcase open, so ignoreOverlap isn't
 * needed here.
 */
export const blocks = (dragged: THREE.Group, other: THREE.Group): boolean =>
    !isSameLayoutItem(other, dragged) &&
    !belongsInside(dragged) &&
    !belongsInside(other);
